import {
    useCallback,
    Fragment,
    useState
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    Modal,
    View
} from "react-native";
import type {
    FormItem
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitToast,
    NCoreUIKitTheme,
    PageContainer,
    TextInput,
    Sticker,
    Switch,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useFocusEffect,
    useNavigation
} from "@react-navigation/native";
import type RootStackParamList from "../../../../navigation/type";
import {
    SearchIcon,
    Share2Icon,
    TrashIcon,
    PlusIcon,
    CopyIcon,
    PenIcon,
    XIcon
} from "lucide-react-native";
import {
    type NativeStackNavigationProp
} from "@react-navigation/native-stack";
import Clipboard from "@react-native-clipboard/clipboard";
import RESTServiceContext from "../../../../services/restAPI/context";

const FormsList = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const [
        forms,
        setForms
    ] = useState<FormItem[]>([]);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const [
        searchQuery,
        setSearchQuery
    ] = useState<string>("");

    const [
        generatedLink,
        setGeneratedLink
    ] = useState<string | null>(null);

    const [
        activeFormId,
        setActiveFormId
    ] = useState<string | null>(null);

    const [
        isOpenedFromToggle,
        setIsOpenedFromToggle
    ] = useState<boolean>(false);

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const fetchForms = useCallback(async () => {
        try {
            setLoading(true);
            const result = await RESTServiceContext.action("getAllFormsForAdmin", null);
            if (result.payload) {
                setForms(result.payload);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useFocusEffect(
        useCallback(() => {
            fetchForms();
        }, [
            fetchForms
        ])
    );

    const handleToggleForm = async (formID: string) => {
        try {
            const result = await RESTServiceContext.action("toggleFormStatusForAdmin", {
                formID
            });
            if (result.payload) {
                setForms(prev => prev.map(form =>
                    form.id === formID ? {
                        ...form,
                        isActive: result.payload.isActive
                    } : form
                ));
                if (result.payload.isActive && result.payload.formLink) {
                    setIsOpenedFromToggle(true);
                    setActiveFormId(formID);
                    setGeneratedLink(result.payload.formLink);
                } else {
                    NCoreUIKitToast.open({
                        title: localize("forms-list-close-success"),
                        type: "success"
                    });
                }
            }
        } catch (error) {
            console.error(error);
            NCoreUIKitToast.open({
                title: localize("forms-error-general"),
                type: "danger"
            });
        }
    };

    const handleSwitchPress = (form: FormItem) => {
        handleToggleForm(form.id);
    };

    const handleSharePress = (form: FormItem) => {
        setIsOpenedFromToggle(false);
        setActiveFormId(form.id);
        if(form.formLink) {
            setGeneratedLink(form.formLink);
        }
    };

    const closeLinkModal = () => {
        setActiveFormId(null);
        setGeneratedLink(null);
    };

    const copyToClipboard = () => {
        if (generatedLink) {
            Clipboard.setString(generatedLink);
            NCoreUIKitToast.open({
                title: localize("forms-list-copy-success"),
                type: "success"
            });
            closeLinkModal();
        }
    };

    const filteredForms = forms.filter(form => {
        const matchesSearch = searchQuery === "" ||
            (form.title?.toLowerCase().includes(searchQuery.toLowerCase()) || false) ||
            (form.description?.toLowerCase().includes(searchQuery.toLowerCase()) || false);

        if (!matchesSearch) {
            return false;
        }

        return true;
    });

    const renderHeader = () => {
        return <View
            style={stylesheet.headerContainer}
        >
            <Text
                variant="titleLargeSize"
            >
                {localize("menu-forms-title")}
            </Text>
            <Button
                onPress={() => navigation.navigate("FormCreate")}
                customTextColor={colors.project.brandWhite}
                icon={({
                    color,
                    size
                }) => <PlusIcon
                    color={colors.content.icon[color]}
                    size={size}
                />}
                title={localize("forms-create-new")}
                spreadBehaviour="free"
                customColor="gold"
            />
        </View>;
    };

    const renderFilters = () => {
        return <View
            style={[
                stylesheet.filterSection,
                {
                    alignItems: isMobile ? "stretch" : "center",
                    flexDirection: isMobile ? "column" : "row",
                    marginTop: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.searchContainer,
                    isMobile && {
                        width: "100%"
                    }
                ]}
            >
                <TextInput
                    placeholder={localize("forms-list-search-placeholder")}
                    customColor={colors.project.brandWhite}
                    icon={({
                        color,
                        size
                    }) => <SearchIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    onChangeText={setSearchQuery}
                    spreadBehaviour="stretch"
                    value={searchQuery}
                />
            </View>
        </View>;
    };

    const renderLinkModal = () => {
        return <Modal
            visible={generatedLink !== null}
            onRequestClose={closeLinkModal}
            animationType="fade"
            transparent={true}
        >
            <View
                style={[
                    stylesheet.modalOverlay,
                    {
                        backgroundColor: colors.content.container.overlay
                    }
                ]}
            >
                <View
                    style={{
                        backgroundColor: colors.project.brandWhite,
                        width: isMobile ? "90%" : 400,
                        padding: spaces.spacingXl,
                        borderRadius: radiuses.lg,
                        gap: spaces.spacingLg
                    }}
                >
                    <View
                        style={stylesheet.modalHeader}
                    >
                        <Text
                            variant="titleMediumSize"
                        >
                            {localize("forms-list-link-title")}
                        </Text>
                        <TouchableOpacity
                            onPress={closeLinkModal}
                        >
                            <XIcon
                                color={colors.content.icon.mid}
                                size={24}
                            />
                        </TouchableOpacity>
                    </View>
                    <Text
                        customColor="content.text.mid"
                        variant="bodyMediumSize"
                    >
                        {localize("forms-list-link-desc")}
                    </Text>
                    <View
                        style={{
                            backgroundColor: colors.content.container.subtle,
                            padding: spaces.spacingMd,
                            borderRadius: radiuses.md
                        }}
                    >
                        <Text
                            customColor="content.text.high"
                            variant="bodySmallSize"
                        >
                            {generatedLink}
                        </Text>
                    </View>
                    <Button
                        title={localize("forms-list-copy-link")}
                        icon={({
                            color,
                            size
                        }) => <CopyIcon
                            color={colors.content.icon[color]}
                            size={size}
                        />}
                        onPress={copyToClipboard}
                        spreadBehaviour="stretch"
                        variant="filled"
                        type="primary"
                    />
                    {
                        (activeFormId && !isOpenedFromToggle) ?
                            <Button
                                title={localize("forms-list-close-share")}
                                icon={({
                                    color,
                                    size
                                }) => <TrashIcon
                                    color={colors.content.icon[color]}
                                    size={size}
                                />}
                                onPress={() => {
                                    handleToggleForm(activeFormId);
                                    closeLinkModal();
                                }}
                                spreadBehaviour="stretch"
                                type="danger"
                            />
                        :
                            null
                    }
                </View>
            </View>
        </Modal>;
    };

    const renderRow = (
        form: FormItem,
        index: number
    ) => {
        const formKey = form.id || `form-${index}`;
        const questionCount = form.questionCount || 0;

        return <TouchableOpacity
            style={[
                stylesheet.rowContainer,
                {
                    alignItems: isMobile ? "flex-start" : "center",
                    flexDirection: isMobile ? "column" : "row",
                    backgroundColor: colors.project.brandWhite,
                    gap: isMobile ? spaces.spacingMd : 0,
                    marginBottom: spaces.spacingMd,
                    padding: spaces.spacingMd,
                    borderRadius: radiuses.md
                }
            ]}
            onPress={() => {
                navigation.navigate("FormDetail", {
                    formID: form.id
                });
            }}
            key={formKey}
        >
            <View
                style={[
                    stylesheet.rowLeftContent,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.rowHeader,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        style={stylesheet.rowTitle}
                        variant="titleMediumSize"
                    >
                        {form.title || localize("forms-list-unnamed-form")}
                    </Text>
                    {
                        form.isReusable === false ?
                            <Sticker
                                title={form.eventName ? `${localize("forms-list-event-bound")} ${form.eventName}` : localize("forms-list-not-reusable")}
                                spreadBehaviour="free"
                                size="small"
                            />
                        :
                            null
                    }
                </View>
                <View
                    style={[
                        stylesheet.rowSubtitle,
                        {
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    <Text
                        customColor="content.text.low"
                        variant="bodySmallSize"
                    >
                        {`${questionCount} ${localize("forms-list-question-count")}`}
                    </Text>
                    <Text
                        customColor="content.text.low"
                        variant="bodySmallSize"
                    >
                        {" • "}
                    </Text>
                    <Text
                        customColor="content.text.low"
                        variant="bodySmallSize"
                    >
                        {`${form.responseCount || 0} ${localize("forms-list-response-count")}`}
                    </Text>
                    {
                        form.isOnlyMembers ?
                            <Fragment>
                                <Text
                                    customColor="content.text.low"
                                    variant="bodySmallSize"
                                >
                                    {" • "}
                                </Text>
                                <Text
                                    customColor="content.text.low"
                                    variant="bodySmallSize"
                                >
                                    {localize("forms-create-only-members")}
                                </Text>
                            </Fragment>
                        :
                            null
                    }
                </View>
            </View>
            <View
                style={[
                    stylesheet.rowRightContent,
                    {
                        justifyContent: isMobile ? "center" : "flex-end",
                        alignSelf: isMobile ? "stretch" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        width: isMobile ? "100%" : "auto",
                        gap: spaces.spacingMd,
                        alignItems: "center"
                    }
                ]}
            >
                <View
                    style={stylesheet.switchContainer}
                >
                    <Switch
                        title={form.isActive ? localize("forms-list-share-open") : localize("forms-list-share-closed")}
                        onPress={() => handleSwitchPress(form)}
                        isDisabled={form.isReusable === false}
                        customTheme={{
                            themeKey: "events_form" as never
                        }}
                        titleStyle={stylesheet.switchTitle}
                        isActive={form.isActive || false}
                        type="neutral"
                    />
                </View>
                {
                    form.isActive ?
                        <Button
                            spreadBehaviour={isMobile ? "stretch" : "free"}
                            title={localize("forms-list-share-button")}
                            icon={({
                                color,
                                size
                            }) => <Share2Icon
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            onPress={() => handleSharePress(form)}
                            customBorderColor="secondaryButton"
                            customColor="secondaryButton"
                            variant="filled"
                        />
                    :
                        null
                }
                <Button
                    spreadBehaviour={isMobile ? "stretch" : "free"}
                    title={localize("forms-list-edit-button")}
                    icon={({
                        color,
                        size
                    }) => <PenIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    onPress={() => {
                        navigation.navigate("FormEdit", {
                            formID: form.id
                        });
                    }}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    variant="filled"
                />
            </View>
        </TouchableOpacity>;
    };

    const renderContent = () => {
        if (loading) {
            return <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <ActivityIndicator
                    color={colors.project.gold}
                    size="large"
                />
            </View>;
        }

        if (filteredForms.length === 0) {
            return <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    style={[
                        stylesheet.emptyText,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyLargeSize"
                >
                    {localize("forms-list-empty-list")}
                </Text>
            </View>;
        }

        return <View
            style={[
                stylesheet.listContainer,
                {
                    marginTop: spaces.spacingLg
                }
            ]}
        >
            {filteredForms.map(renderRow)}
        </View>;
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            {renderHeader()}
            {renderFilters()}
            {renderContent()}
        </View>
        {renderLinkModal()}
    </PageContainer>;
};

export default FormsList;
